import { useDraggable } from '@dnd-kit/core';
import {
  CalculateOutlined,
  CheckCircleOutlineRounded,
  DeleteOutline as DeleteOutlineIcon,
  KeyboardArrowDownRounded,
  LinkRounded,
  SearchRounded,
  TuneRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  IconButton,
  InputBase,
  Skeleton,
  Stack,
  Tooltip,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { CSSProperties, FC, KeyboardEvent, memo, useCallback, useEffect, useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import { distanceKmText, durationHoursText, quantityValueText, volumeValueText, weightValueText } from 'src/components/dispatch/metricText';
import {
  EMPTY_ORDER_ATTR_FILTER, filterUnassignedItemsByOrderAttrs, OrderAttrFilter,
} from 'src/components/dispatch/orderAttrFilter';
import { sumDistanceMm } from 'src/components/dispatch/summaryDistance';
import { loadingRatePercent, ratePercentText, sumCapacityQuantity, sumLegRunAmounts, sumPeakLoads } from 'src/components/dispatch/summaryLoad';
import { DisplayMetric } from 'src/components/dispatch/unassignedDisplay/displayMetricKeys';
import { LaneCard } from 'src/components/dispatch/unassignedDisplay/LaneCard';
import { buildLaneDetailItem } from 'src/components/dispatch/unassignedDisplay/laneDetailItem';
import { buildLaneGroups, mergeLaneNotAllocReasons, sortLaneGroups } from 'src/components/dispatch/unassignedDisplay/laneGroups';
import { AdvancedFilterPopover, sortBtnSx } from 'src/components/dispatch/unassignedFilters/AdvancedFilterPopover';
import { buildFilterColumns } from 'src/components/dispatch/unassignedFilters/columns';
import { evaluateFilters, hasActiveFilter } from 'src/components/dispatch/unassignedFilters/evaluate';
import { matchesFreeText } from 'src/components/dispatch/unassignedFilters/freeTextSearch';
import { buildMapScenarioFilter, cloneMapScenarioFilter, filterActiveRows } from 'src/components/dispatch/unassignedFilters/scenarioFilter';
import { FilterColumnDef, FilterRow, MapScenarioFilterV1 } from 'src/components/dispatch/unassignedFilters/types';
import { useServerFilterPresets } from 'src/components/dispatch/unassignedFilters/useServerFilterPresets';
import { UnassignedItem } from 'src/entities/dispatch';
import { DeliveryListGroupingValues } from 'src/entities/licenseEntity';
import { PlanningsDeliveryEntity, PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsNotAllocReason } from 'src/entities/PlanningsNotAllocReasons.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';
import { useComposition } from 'src/hooks/useComposition';
import { usePersistedBoolean } from 'src/hooks/usePersistedBoolean';
import { usePersistedJSON } from 'src/hooks/usePersistedJSON';

import { VehicleRowGroup } from './adapter';
import { UnassignedCardContent } from './UnassignedCardContent';
import { UnassignedDropArea } from './UnassignedDropArea';



/**
 * 32 列フィルタは元データ（legRun）に対して評価する。
 * source を持たないアイテムは、有効なフィルタがあるときは通さない（評価できないため）。
 */
export function filterItemsByRows(
  items: UnassignedItem[],
  rows: FilterRow[],
  columnsByKey: Map<string, FilterColumnDef>,
): UnassignedItem[] {
  if (!hasActiveFilter(rows, columnsByKey)) return items;
  return items.filter((item) => (item.source ? evaluateFilters(item.source, rows, columnsByKey) : false));
}

/** 選択済みのうち、現在のフィルタで非表示になっている件数。 */
export function countHiddenSelected(
  items: UnassignedItem[],
  filteredItems: UnassignedItem[],
  selectedItemIds: Set<string>,
): number {
  if (selectedItemIds.size === 0) return 0;
  const visible = new Set(filteredItems.map((i) => i.id));
  return items.filter((i) => selectedItemIds.has(i.id) && !visible.has(i.id)).length;
}

type SortField = 'quantity' | 'weight' | 'volume';
type SortOrder = 'asc' | 'desc';
type StatusLevel = 'ok' | 'critical';

type DisplayMode = 'invoice' | 'lane';
const isDisplayMode = (parsed: unknown): parsed is DisplayMode => parsed === 'invoice' || parsed === 'lane';

/* ─── DispatchStatusCard ─── */
interface DispatchStatusCardProps {
  /** 当日案件総量 = assigned + unassigned */
  totalCargoCases: number;
  totalCargoWeightKg: number;
  totalCargoVolumeM3: number;
  /** 配車車両の総キャパ。null は集計不能 (該当軸の表示・判定をスキップ) */
  totalCapacityCases: number | null;
  totalCapacityKg: number | null;
  totalCapacityM3: number | null;
  uniqueTruckCount: number;
  totalRotations: number;
  /**
   * 配車不成立 = 当日案件総量 (assigned + unassigned) のいずれか (数量・重量・体積) が
   * トラック総キャパを超えている場合。判定ロジックは親で行い、結果のみを渡す
   */
  isFeasible: boolean;
  /** どのメトリクスを表示するか。displayMetrics に応じて 数量/重量/体積 の値を絞る */
  displayMetrics: Set<DisplayMetric>;
  /** GA の screen_name。/map と convenience で異なるため呼び出し側から渡す */
  screenName: string;
}

/** 数量/重量/体積を displayMetrics に従って表示する。
 *  / は割り算と紛らわしいので使わず、各値を span で並べて gap (CSS) で間隔を確保する。 */
function TriMetricValue({
  metrics,
  cases,
  weightKg,
  volumeM3,
}: {
  metrics: Set<DisplayMetric>;
  cases: number;
  weightKg: number;
  volumeM3: number;
}) {
  const parts: string[] = [];
  if (metrics.has('quantity')) parts.push(`${quantityValueText(cases)}cs`);
  if (metrics.has('weight')) parts.push(`${weightValueText(weightKg)}kg`);
  if (metrics.has('volume')) parts.push(`${volumeValueText(volumeM3)}m³`);
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', columnGap: 1.25 }}>
      {parts.map((p) => (
        <Box component="span" key={p}>{p}</Box>
      ))}
    </Box>
  );
}

const DispatchStatusCard: FC<DispatchStatusCardProps> = ({
  totalCargoCases,
  totalCargoWeightKg,
  totalCargoVolumeM3,
  totalCapacityCases,
  totalCapacityKg,
  totalCapacityM3,
  uniqueTruckCount,
  totalRotations,
  isFeasible,
  displayMetrics,
  screenName,
}) => {
  const [isOpen, setIsOpen] = usePersistedBoolean('convini.statusCard.open', true);
  const level: StatusLevel = isFeasible ? 'ok' : 'critical';
  // 配車可否のみを伝える。未割当件数は本カードでは扱わない (UnassignedStatsCard 側で表示)。
  const headline = level === 'ok' ? '配車可能' : '配車不成立';

  // 不足は displayMetrics に含まれていて、かつ capacity が分かる軸のみ計算する
  const shortageCases = totalCapacityCases != null ? Math.max(0, totalCargoCases - totalCapacityCases) : 0;
  const shortageWeightKg = totalCapacityKg != null ? Math.max(0, totalCargoWeightKg - totalCapacityKg) : 0;
  const shortageVolumeM3 = totalCapacityM3 != null ? Math.max(0, totalCargoVolumeM3 - totalCapacityM3) : 0;
  const shortageParts: string[] = [];
  if (displayMetrics.has('quantity') && shortageCases > 0) {
    shortageParts.push(`${quantityValueText(shortageCases)}cs`);
  }
  if (displayMetrics.has('weight') && shortageWeightKg > 0) {
    shortageParts.push(`${weightValueText(shortageWeightKg)}kg`);
  }
  if (displayMetrics.has('volume') && shortageVolumeM3 > 0) {
    shortageParts.push(`${volumeValueText(shortageVolumeM3)}m³`);
  }
  const hasShortage = shortageParts.length > 0;

  const levelStyles: Record<StatusLevel, { bg: string; border: string; text: string; iconColor: string }> = {
    ok: {
      bg: 'hsl(var(--muted) / 0.5)',
      border: 'hsl(var(--border))',
      text: 'hsl(var(--muted-foreground))',
      iconColor: 'hsl(var(--muted-foreground))',
    },
    critical: {
      bg: 'hsl(var(--destructive) / 0.05)',
      border: 'hsl(var(--destructive) / 0.3)',
      text: 'hsl(var(--destructive))',
      iconColor: 'hsl(var(--destructive))',
    },
  };
  const s = levelStyles[level];
  const rowSx = { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', fontSize: '11px' } as const;
  const valueSx = { fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'hsl(var(--foreground))' } as const;

  return (
    <Box sx={{ borderRadius: 1.5, border: `1px solid ${s.border}`, bgcolor: s.bg, px: 2, py: 1.5 }}>
      <Box
        component="button"
        type="button"
        onClick={() => setIsOpen((o) => {
          const next = !o;
          ReactGA.event('click', {
            screen_name: screenName,
            button_name: '配車可否カード 開閉',
            label: next ? 'OPEN' : 'CLOSE',
          });
          return next;
        })}
        sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'transparent', border: 'none', cursor: 'pointer', p: 0 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <Box component="h3" sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '-0.01em', color: 'hsl(var(--foreground))', flexShrink: 0, m: 0 }}>本日の配車可否</Box>
          {!isOpen && (
            <Box component="span" sx={{ fontSize: '10px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: s.text }}>
              — {headline}
            </Box>
          )}
        </Box>
        <KeyboardArrowDownRounded sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0, transform: isOpen ? 'none' : 'rotate(-90deg)', transition: 'transform 0.2s' }} />
      </Box>

      <Collapse in={isOpen}>
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.75, color: s.text }}>
          {level === 'ok' ? (
            <CheckCircleOutlineRounded sx={{ fontSize: 14, color: s.iconColor }} />
          ) : (
            <WarningAmberRounded sx={{ fontSize: 14, color: s.iconColor }} />
          )}
          <Box component="span" sx={{ fontSize: 12, fontWeight: 600 }}>{headline}</Box>
        </Box>

        <Box sx={{ mt: 1.25, '& > * + *': { mt: 0.5 } }}>
          {hasShortage && (
            <Box sx={rowSx}>
              <Box component="span" sx={{ color: 'hsl(var(--muted-foreground))' }}>不足</Box>
              <Box component="span" sx={{ ...valueSx, color: s.text, display: 'inline-flex', alignItems: 'baseline', columnGap: 1.25 }}>
                {shortageParts.map((p) => (
                  <Box component="span" key={p}>{p}</Box>
                ))}
              </Box>
            </Box>
          )}
          <Box sx={rowSx}>
            <Box component="span" sx={{ color: 'hsl(var(--muted-foreground))' }}>総荷物</Box>
            <Box component="span" sx={valueSx}>
              <TriMetricValue
                metrics={displayMetrics}
                cases={totalCargoCases}
                weightKg={totalCargoWeightKg}
                volumeM3={totalCargoVolumeM3}
              />
            </Box>
          </Box>
          <Box sx={rowSx}>
            <Box component="span" sx={{ color: 'hsl(var(--muted-foreground))' }}>総キャパ</Box>
            <Box component="span" sx={valueSx}>
              <TriMetricValue
                metrics={displayMetrics}
                cases={totalCapacityCases ?? 0}
                weightKg={totalCapacityKg ?? 0}
                volumeM3={totalCapacityM3 ?? 0}
              />
            </Box>
          </Box>
          <Box sx={rowSx}>
            <Box component="span" sx={{ color: 'hsl(var(--muted-foreground))' }}>使用台数</Box>
            <Box component="span" sx={valueSx}>
              {uniqueTruckCount}台{totalRotations > uniqueTruckCount && `（${totalRotations}便）`}
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

/* ─── DispatchSummaryCard (配車サマリ) ─── */
interface DispatchSummaryCardProps {
  /** 配車済 legRun の合計数量 (cs) */
  totalQuantity: number;
  /** 配車済 legRun の合計重量 (kg) */
  totalWeightKg: number;
  /** 配車済 legRun の合計体積 (m³) */
  totalVolumeM3: number;
  /** 積載率の分子。同時に載っている量のピーク (cs)。旧 /plans と同じ定義 */
  peakQuantity: number;
  /** 積載率の分子。同時に載っている量のピーク (kg) */
  peakWeightKg: number;
  /** 積載率の分子。同時に載っている量のピーク (m³) */
  peakVolumeM3: number;
  /** 配車済勤務の合計数量キャパ (cs)。null は不明 */
  totalCapacityCases: number | null;
  /** 配車済車両の合計重量キャパ (kg)。null は不明 */
  totalCapacityKg: number | null;
  /** 配車済車両の合計体積キャパ (m³)。null は不明 */
  totalCapacityM3: number | null;
  /** 売上 (¥)。データなしは 0 */
  totalRevenueYen: number;
  /** 支払 (¥)。データなしは 0 */
  totalPaymentYen: number;
  /** 距離 (km) */
  totalDistanceKm: number;
  /** 時間 (h、小数点 1 桁) */
  totalDurationHours: number;
  /** GA の screen_name。/map と convenience で異なるため呼び出し側から渡す */
  screenName: string;
  /** 運賃計算ダイアログを開く。未指定ならボタンを描かない */
  onOpenTariffCalculation?: () => void;
  /** 非空文字列ならボタンを無効化し、Tooltip にその理由を表示する */
  tariffCalculationDisabledReason?: string;
}

const yenFormatter = new Intl.NumberFormat('ja-JP');

export const DispatchSummaryCard: FC<DispatchSummaryCardProps> = ({
  totalQuantity,
  totalWeightKg,
  totalVolumeM3,
  peakQuantity,
  peakWeightKg,
  peakVolumeM3,
  totalCapacityCases,
  totalCapacityKg,
  totalCapacityM3,
  totalRevenueYen,
  totalPaymentYen,
  totalDistanceKm,
  totalDurationHours,
  screenName,
  onOpenTariffCalculation,
  tariffCalculationDisabledReason,
}) => {
  const [isOpen, setIsOpen] = usePersistedBoolean('convini.summaryCard.open', true);
  // 率の分子は総荷物量ではなくピーク積載量（旧 /plans と同じ）。総荷物量にすると、
  // 回転がある日は同時に載らない量まで数えて実態より高く出る
  const quantityRate = loadingRatePercent(peakQuantity, totalCapacityCases);
  const weightRate = loadingRatePercent(peakWeightKg, totalCapacityKg);
  const volumeRate = loadingRatePercent(peakVolumeM3, totalCapacityM3);
  const profitYen = totalRevenueYen - totalPaymentYen;

  // 折り畳み時の inline summary (サンプル準拠): "1,350cs / 24,819kg / 3,217m³"
  const foldedSummary = [
    `${quantityValueText(totalQuantity)}cs`,
    `${weightValueText(totalWeightKg)}kg`,
    `${volumeValueText(totalVolumeM3)}m³`,
  ].join(' / ');

  const rowSx = { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', fontSize: '11px' } as const;
  const labelSx = { color: 'hsl(var(--muted-foreground))' } as const;
  const valueSx = { fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'hsl(var(--foreground))' } as const;
  const subSx = { ml: 0.75, fontSize: '10px', fontWeight: 400, color: 'hsl(var(--muted-foreground))' } as const;
  // 値の桁数で積載率の位置がずれないよう、積載率は固定幅の枠に左寄せで置く
  const metricSx = { display: 'flex', alignItems: 'baseline', minWidth: 0 } as const;
  const rateSx = { ...subSx, width: 58, flexShrink: 0, textAlign: 'left', whiteSpace: 'nowrap' } as const;
  const dividerSx = { my: 0.75, borderTop: '1px solid hsl(var(--border) / 0.5)' } as const;

  return (
    <Box sx={{ borderRadius: 1.5, border: '1px solid hsl(var(--border))', bgcolor: 'hsl(var(--muted) / 0.5)' }}>
      <Box sx={{ px: 2, py: 1.25 }}>
        <Box
          component="button"
          type="button"
          onClick={() => setIsOpen((o) => {
            const next = !o;
            ReactGA.event('click', {
              screen_name: screenName,
              button_name: '配車サマリカード 開閉',
              label: next ? 'OPEN' : 'CLOSE',
            });
            return next;
          })}
          sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'transparent', border: 'none', cursor: 'pointer', p: 0 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Box component="h3" sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '-0.01em', color: 'hsl(var(--foreground))', flexShrink: 0, m: 0 }}>
              配車サマリ
            </Box>
            {!isOpen && (
              <Box component="span" sx={{ fontSize: '10px', color: 'hsl(var(--muted-foreground))', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                — {foldedSummary}
              </Box>
            )}
          </Box>
          <KeyboardArrowDownRounded sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0, transform: isOpen ? 'none' : 'rotate(-90deg)', transition: 'transform 0.2s' }} />
        </Box>
      </Box>
      <Collapse in={isOpen}>
        <Box sx={{ px: 2, pb: 1.25, '& > * + *': { mt: 0.5 } }}>
          <Box sx={rowSx}>
            <Box component="span" sx={labelSx}>数量</Box>
            <Box sx={metricSx}>
              <Box component="span" sx={valueSx}>{quantityValueText(totalQuantity)}cs</Box>
              <Box component="span" data-testid="summary-quantity-rate" sx={rateSx}>積載率 {ratePercentText(quantityRate)}%</Box>
            </Box>
          </Box>
          <Box sx={rowSx}>
            <Box component="span" sx={labelSx}>重量</Box>
            <Box sx={metricSx}>
              <Box component="span" sx={valueSx}>{weightValueText(totalWeightKg)}kg</Box>
              <Box component="span" data-testid="summary-weight-rate" sx={rateSx}>積載率 {ratePercentText(weightRate)}%</Box>
            </Box>
          </Box>
          <Box sx={rowSx}>
            <Box component="span" sx={labelSx}>体積</Box>
            <Box sx={metricSx}>
              <Box component="span" sx={valueSx}>{volumeValueText(totalVolumeM3)}m³</Box>
              <Box component="span" data-testid="summary-volume-rate" sx={rateSx}>積載率 {ratePercentText(volumeRate)}%</Box>
            </Box>
          </Box>

          <Box sx={dividerSx} />
          {onOpenTariffCalculation && (
            // 「出るのに押せないボタン」ではなく「渡されなければ何も出ない」設計。
            // 見出しラベルだけが浮くのを避けるため、ラベルごと条件分岐に入れる
            // (DetailPanelShell.tsx:113-136 の editButton と同じ考え方)。
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
              <Box component="span" sx={{ fontSize: '11px', fontWeight: 600, color: 'hsl(var(--muted-foreground))' }}>
                運賃
              </Box>
              {/* MUI の disabled なボタンは hover を発火しないため、Tooltip を効かせるには span で包む
                  (DetailPanelShell.tsx:123 と同じ形) */}
              <Tooltip title={tariffCalculationDisabledReason || '運賃計算を実行'} arrow>
                <span>
                  <IconButton
                    size="small"
                    disabled={Boolean(tariffCalculationDisabledReason)}
                    onClick={() => {
                      ReactGA.event('click', { screen_name: screenName, button_name: '運賃計算' });
                      onOpenTariffCalculation();
                    }}
                    aria-label="運賃計算"
                    sx={{ width: 24, height: 24, borderRadius: 1, border: '1px solid hsl(var(--border))' }}
                  >
                    <CalculateOutlined sx={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
          <Box sx={rowSx}>
            <Box component="span" sx={labelSx}>売上</Box>
            <Box component="span" sx={valueSx}>¥{yenFormatter.format(totalRevenueYen)}</Box>
          </Box>
          <Box sx={rowSx}>
            <Box component="span" sx={labelSx}>支払</Box>
            <Box component="span" sx={valueSx}>¥{yenFormatter.format(totalPaymentYen)}</Box>
          </Box>
          <Box sx={rowSx}>
            <Box component="span" sx={labelSx}>利益</Box>
            <Box component="span" sx={valueSx}>¥{yenFormatter.format(profitYen)}</Box>
          </Box>

          <Box sx={dividerSx} />
          <Box sx={rowSx}>
            <Box component="span" sx={labelSx}>距離</Box>
            <Box component="span" sx={valueSx}>{distanceKmText(totalDistanceKm)}km</Box>
          </Box>
          <Box sx={rowSx}>
            <Box component="span" sx={labelSx}>時間</Box>
            <Box component="span" sx={valueSx}>{durationHoursText(totalDurationHours)}h</Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

/* ─── UnassignedStatsCard ─── */
interface UnassignedStatsCardProps {
  items: UnassignedItem[];
  selectedItems: UnassignedItem[];
  /** GA の screen_name。/map と convenience で異なるため呼び出し側から渡す */
  screenName: string;
}

const UnassignedStatsCard: FC<UnassignedStatsCardProps> = ({ items, selectedItems, screenName }) => {
  const [isOpen, setIsOpen] = usePersistedBoolean('convini.unassignedCard.open', true);

  // legRun の実値がある case のみ合算する (null は 0 として扱う)。
  // cases × 係数の推定値は使わない。
  const sumOf = (list: UnassignedItem[]) => ({
    quantity: list.reduce((s, it) => s + (it.cases ?? 0), 0),
    weightKg: list.reduce((s, it) => s + (it.weight ?? 0), 0),
    volumeM3: list.reduce((s, it) => s + (it.volume ?? 0), 0),
  });
  const totals = useMemo(() => sumOf(items), [items]);
  const picked = useMemo(() => sumOf(selectedItems), [selectedItems]);

  const rowSx = { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', fontSize: '11px' } as const;
  const valueSx = { fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'hsl(var(--foreground))' } as const;

  return (
    <Box sx={{ borderRadius: 1.5, border: '1px solid hsl(var(--border))', bgcolor: 'hsl(var(--muted) / 0.5)' }}>
      <Box sx={{ px: 2, py: 1.25 }}>
        <Box
          component="button"
          type="button"
          onClick={() => setIsOpen((o) => {
            const next = !o;
            ReactGA.event('click', {
              screen_name: screenName,
              button_name: '未割当統計カード 開閉',
              label: next ? 'OPEN' : 'CLOSE',
            });
            return next;
          })}
          sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'transparent', border: 'none', cursor: 'pointer', p: 0 }}
        >
          <Box component="h3" sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '-0.01em', color: 'hsl(var(--foreground))', m: 0 }}>
            未割当（{items.length}件）
          </Box>
          <KeyboardArrowDownRounded sx={{ fontSize: 14, color: 'text.secondary', transform: isOpen ? 'none' : 'rotate(-90deg)', transition: 'transform 0.2s' }} />
        </Box>
      </Box>
      <Collapse in={isOpen}>
        <Box sx={{ px: 2, pb: 1.25, '& > * + *': { mt: 0.5 } }}>
          <Box sx={rowSx}>
            <Box component="span" sx={{ color: 'hsl(var(--muted-foreground))' }}>数量</Box>
            <Box component="span" sx={valueSx}>
              {quantityValueText(picked.quantity)} / {quantityValueText(totals.quantity)}cs
            </Box>
          </Box>
          <Box sx={rowSx}>
            <Box component="span" sx={{ color: 'hsl(var(--muted-foreground))' }}>重量</Box>
            <Box component="span" sx={valueSx}>
              {weightValueText(picked.weightKg)} / {weightValueText(totals.weightKg)}kg
            </Box>
          </Box>
          <Box sx={rowSx}>
            <Box component="span" sx={{ color: 'hsl(var(--muted-foreground))' }}>体積</Box>
            <Box component="span" sx={valueSx}>
              {volumeValueText(picked.volumeM3)} / {volumeValueText(totals.volumeM3)}m³
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

/* ─── DraggableCardItem ─── */
interface DraggableCardItemProps {
  item: UnassignedItem;
  displayMetrics: Set<DisplayMetric>;
  selected: boolean;
  onSelectItem: (id: string) => void;
  /** GA の screen_name。/map と convenience で異なるため呼び出し側から渡す */
  screenName: string;
  /** /map ではドラッグ割当を出さない（DndContext が無く、掴めるのに動かない状態になる） */
  readOnly?: boolean;
  /** 未指定なら詳細ボタンを描かない（/map は詳細パネル本体が P3 のため渡さない） */
  onShowDetail?: (item: UnassignedItem, rect: DOMRect) => void;
  notAllocReasons?: PlanningsNotAllocReason[];
  notAllocDeliveries?: PlanningsDeliveryEntity[];
  notAllocTrucks?: PlanningsTruckEntity[];
  notAllocDrivers?: PlanningsDriverEntity[];
  onRestoreSplit?: (item: UnassignedItem) => void;
  /** 指定時は「分割を戻す」ボタンを disabled にし、素の title 属性で理由を出す（Task 10 #3）。 */
  restoreSplitDisabledReason?: string;
  /** 会社設定の任意項目名。任意項目キーの列挙に使う */
  customInputFieldNames: string[];
}

/**
 * D&D 登録と選択操作だけを持つ薄いラッパー。重い JSX は memo 済みの
 * UnassignedCardContent（中身）へ渡す。selected は中身に渡さない
 * （選択枠は自分の sx にしか使わないので、渡すと選択のたびに中身まで再描画される）。
 */
export const DraggableCardItem: FC<DraggableCardItemProps> = memo(({
  item,
  selected,
  onSelectItem,
  screenName,
  readOnly,
  ...contentProps
}) => {
  // disabled で listeners が undefined になり、登録状態を含めて D&D 無効になる。
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: item.id,
    data: { unassigned: true },
    disabled: readOnly,
  });

  // ドラッグ中はポインタ移動ごとにここが再描画される（useDraggable が InternalContext を
  // useContext で購読するため。over を含む値を毎回読み直すので、memo では避けられない。
  // dnd-kit のインストール済みソース（core.cjs.development.js の useDraggable 実装）で
  // 確認済み）。ここで作り直すのは style と sx だけにし、大きな JSX は memo 済みの
  // UnassignedCardContent に任せる。
  const style: CSSProperties = useMemo(() => ({
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.5 : 1,
  }), [transform, isDragging]);

  const handleClick = useCallback(() => {
    ReactGA.event('click', { screen_name: screenName, button_name: '未割当カード 選択' });
    onSelectItem(item.id);
  }, [screenName, onSelectItem, item.id]);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    handleClick();
  }, [handleClick]);

  const sx = useMemo(() => ({
    borderRadius: 1,
    bgcolor: 'hsl(var(--card))',
    p: 1.5,
    cursor: readOnly ? 'pointer' : 'grab',
    userSelect: 'none',
    boxShadow: selected
      ? '0 0 0 2px hsl(var(--primary)), 0 2px 4px hsl(var(--foreground)/0.04)'
      : '0 0 0 1px hsl(var(--border)/0.7), 0 2px 4px hsl(var(--foreground)/0.04)',
    transition: 'box-shadow 0.15s',
    ...(readOnly ? {} : { '&:active': { cursor: 'grabbing' } }),
  }), [selected, readOnly]);

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...(readOnly ? {} : attributes)}
      {...listeners}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      sx={sx}
    >
      <UnassignedCardContent item={item} screenName={screenName} {...contentProps} />
    </Box>
  );
});

/* ─── Main ─── */
type Props = {
  items: UnassignedItem[];
  rowGroups: VehicleRowGroup[];
  /** 配車サマリ計算用 (実重量・売上・距離など)。未指定なら集計しない */
  deliveries?: PlanningsDeliveryWithLegRunEntity[];
  /**
   * 表示対象（配送形態・区間種別）。非表示にしている値の案件を一覧から落とす。
   * 未指定なら絞り込まない。サマリは絞り込み前の全件で出す（詳細フィルターと同じ扱い）。
   */
  orderAttrFilter?: OrderAttrFilter;
  isLoading: boolean;
  displayMetrics: Set<DisplayMetric>;
  selectedItemIds: Set<string>;
  onSelectItem: (id: string) => void;
  /** 案件カード右側の info ボタンクリック時 → 詳細パネルを開く。未指定なら詳細ボタンを描かない
   *  (/map は詳細パネル本体が P3 のため渡さない) */
  onShowDetail?: (item: UnassignedItem, rect: DOMRect) => void;
  /** GA の screen_name。/map と convenience で異なるため呼び出し側から渡す */
  screenName: string;
  /** 自動配車後の未割当理由: order_id (legRunId) → 理由群 */
  notAllocReasonsByOrderId?: Map<number, PlanningsNotAllocReason[]>;
  notAllocDeliveries?: PlanningsDeliveryEntity[];
  notAllocTrucks?: PlanningsTruckEntity[];
  notAllocDrivers?: PlanningsDriverEntity[];
  /** 「固定ルートに対応づけ」ボタンの実装。未指定/fixedRoute 未選択時は disabled */
  onAlignToFixedRoute?: () => void;
  isAligningToFixedRoute?: boolean;
  /** 選択中の未割当案件をまとめて削除する (DELETE /api/v5/shipment_orders/bulk) */
  onDeleteSelected?: () => void;
  /** 分割済みアイコンクリック時に呼ばれる復元コールバック */
  onRestoreSplit?: (item: UnassignedItem) => void;
  /** 指定時は「分割を戻す」ボタンを disabled にし、素の title 属性で理由を出す（Task 10 #3）。 */
  restoreSplitDisabledReason?: string;
  /** true で P0 対象外の編集操作 (選択削除/固定ルートに対応づけ) を非表示にする。default false */
  readOnly?: boolean;
  /**
   * エンジン設定「表示設定 > 配送リストの地点表示」。groupByName なら「地点単位」表示で
   * 同一座標でも納品先名称が違うレーンを分ける（依頼 log_web_issues#299-2）。
   */
  deliveryListGrouping?: DeliveryListGroupingValues;
  /**
   * true で「伝票単位 / 地点単位」の表示単位トグルを出す。default false。
   * convenience は同じ行に「固定ルートに対応づけ」があるため出さない（ユーザー判断 2026-07-29）。
   * false のときは localStorage の保存値に関わらず伝票単位に固定する
   * （キーは /map と共有なので、トグルが無い画面で地点単位に固定されると抜け出せなくなる）。
   */
  displayModeSwitchable?: boolean;
  /** license.custom_input_fields（会社ごとの任意項目名）。32 列フィルタの任意項目列に使う */
  customInputFieldNames: string[];
  /** 32 列フィルタの「指定トラック」列の選択肢 */
  trucksForFilter: { id: number; name: string }[];
  /** 32 列フィルタの「NGドライバー」列の選択肢 */
  driversForFilter: { id: number; name: string }[];
  /** フィルタプリセットの保存先を会社単位で分けるためのキー */
  companyId: string;
  /** license 設定の取得完了 (custom_input_fields 列の検証に使う) */
  isLicenseReady: boolean;
  /** trucks/drivers マスタの取得完了 (指定トラック/NGドライバー列の検証に使う) */
  isMastersReady: boolean;
  /** マスタ取得が失敗したか。既定 false（= エラーなし）が意味的に安全なので optional */
  isMastersError?: boolean;
  /** 非表示になった選択件数が変わったときに呼ばれるコールバック */
  onHiddenSelectedCountChange?: (count: number) => void;
  /** 運賃計算ダイアログを開く。未指定なら配車サマリカードにボタンを描かない */
  onOpenTariffCalculation?: () => void;
  /** 非空文字列なら配車サマリカードの運賃計算ボタンを無効化し、Tooltip にその理由を表示する */
  tariffCalculationDisabledReason?: string;
  /**
   * シナリオ保存用: 32 列フィルタ（フリーワード + 有効な行）のスナップショットが変わるたびに通知する。
   * 未指定なら何もしない（/convenience_dispatches は渡さない。渡さない場合に既存の挙動は変えない）。
   */
  onScenarioFilterChange?: (filter: MapScenarioFilterV1) => void;
};

const ConvenienceDispatchUnassignedList: FC<Props> = memo(({
  items,
  rowGroups,
  deliveries,
  orderAttrFilter = EMPTY_ORDER_ATTR_FILTER,
  isLoading,
  displayMetrics,
  selectedItemIds,
  onSelectItem,
  onShowDetail,
  screenName,
  notAllocReasonsByOrderId,
  notAllocDeliveries,
  notAllocTrucks,
  notAllocDrivers,
  onAlignToFixedRoute,
  isAligningToFixedRoute = false,
  onDeleteSelected,
  onRestoreSplit,
  restoreSplitDisabledReason,
  readOnly = false,
  displayModeSwitchable = false,
  deliveryListGrouping = 'groupByAddress',
  customInputFieldNames,
  trucksForFilter,
  driversForFilter,
  companyId,
  isLicenseReady,
  isMastersReady,
  isMastersError = false,
  onHiddenSelectedCountChange,
  onOpenTariffCalculation,
  tariffCalculationDisabledReason,
  onScenarioFilterChange,
}) => {
  // 入力値と絞り込み値を分ける。IME 変換中（未確定）は入力欄の表示だけ追随させ、
  // 絞り込みには反映しない。確定するまで絞ると、変換候補を選ぶ前にリストがちらつく。
  // 入力自体は止めない（止めると文字が打てなくなる）。
  const [searchText, setSearchText] = useState('');
  const [committedSearchText, setCommittedSearchText] = useState('');
  const { isComposing, onCompositionStart, onCompositionEnd } = useComposition();

  const handleSearchChange = useCallback((value: string) => {
    setSearchText(value);
    // IME を通さない入力（英数字・貼り付け）は compositionStart が来ないので、
    // isComposing だけを見ると永久に絞り込まれない
    if (!isComposing) setCommittedSearchText(value);
  }, [isComposing]);

  const handleCompositionEnd = useCallback((value: string) => {
    onCompositionEnd();
    setCommittedSearchText(value);
  }, [onCompositionEnd]);
  const [sortField, setSortField] = useState<SortField>('quantity');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  // displayMode は未割当パネル固有の関心事なのでリスト内部で持つ（sortField / advancedFilters と同じ）。
  const [storedDisplayMode, setDisplayMode] = usePersistedJSON<DisplayMode>(
    'dispatch.unassigned.displayMode',
    'invoice',
    isDisplayMode,
  );
  // トグルを出さない画面では保存値を無視して伝票単位に固定する。localStorage キーは
  // /map と共有なので、そうしないとトグルの無い画面が地点単位のまま抜け出せなくなる。
  const displayMode: DisplayMode = displayModeSwitchable ? storedDisplayMode : 'invoice';
  const [advancedFilters, setAdvancedFilters] = useState<FilterRow[]>([]);
  // 会社切替 (SwitchCompanies) は licenseContext.update() + queryClient.clear() のみで
  // この画面をアンマウントしない。適用済みの条件行を残すと、別会社のデータ
  // (trucks/drivers/custom_input_fields が別物) に対して評価され続けてしまうため、
  // companyId が変わったら破棄する (AdvancedFilterPopover 内の presets/pending と同じ考え方)。
  useEffect(() => {
    setAdvancedFilters([]);
  }, [companyId]);
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const filterOpen = Boolean(filterAnchor);
  const { enqueueSnackbar } = useSnackbar();

  const filterColumns = useMemo(
    () => buildFilterColumns({ customInputFieldNames, trucks: trucksForFilter, drivers: driversForFilter }),
    [customInputFieldNames, trucksForFilter, driversForFilter],
  );
  const filterColumnsByKey = useMemo(() => new Map(filterColumns.map((c) => [c.key, c])), [filterColumns]);

  // プリセットはサーバ保存（会社共有）。localStorage には持たない
  const serverPresets = useServerFilterPresets(companyId);
  const handleSavePreset = useCallback((name: string, rows: FilterRow[]) => {
    serverPresets.savePreset(name, rows, {
      onError: () => enqueueSnackbar('フィルターの保存に失敗しました', { variant: 'error' }),
    });
  }, [enqueueSnackbar, serverPresets]);
  const handleDeletePreset = useCallback((name: string) => {
    const target = serverPresets.presets.find((p) => p.name === name);
    if (!target) return;
    serverPresets.deletePreset(target, {
      onError: () => enqueueSnackbar('フィルターの削除に失敗しました', { variant: 'error' }),
    });
  }, [enqueueSnackbar, serverPresets]);

  // 件数バッジ。評価・プリセット保存・シナリオ保存(onScenarioFilterChange)と同じ判定を使う
  // （filterActiveRows に一本化し、箇所ごとに食い違わせない）
  const activeFilterCount = useMemo(
    () => filterActiveRows(advancedFilters, filterColumnsByKey).length,
    [advancedFilters, filterColumnsByKey],
  );

  // シナリオ保存用: フリーワード（確定値のみ）+ 有効なフィルタ行のスナップショットを親へ通知する。
  // sort・displayMode・popover 開閉は保存対象に含めない。受信側が返したオブジェクトを書き換えても
  // 内部 state（advancedFilters）に影響しないよう、渡す直前に deep copy する。
  useEffect(() => {
    if (!onScenarioFilterChange) return;
    const snapshot = buildMapScenarioFilter(committedSearchText, advancedFilters, filterColumnsByKey);
    onScenarioFilterChange(cloneMapScenarioFilter(snapshot));
  }, [committedSearchText, advancedFilters, filterColumnsByKey, onScenarioFilterChange]);

  // 統計（車両側から算出）
  const realRows = useMemo(() => rowGroups.flatMap((g) => g.rows.filter((r) => !r.source.isPlaceholderRow)), [rowGroups]);
  const uniqueTruckCount = useMemo(() => new Set(realRows.map((r) => r.truckId)).size, [realRows]);
  const totalRotations = realRows.length;

  /**
   * 配車可否カードと不成立判定に使うキャパ合計。
   *
   * どれか 1 台でも未設定なら、その軸は集計不能 (null) として判定から外す。当日の全荷物を
   * 積みきれるかという話なので、未設定の車両を 0 と見なして「不成立」を出してはいけない。
   *
   * Why not 積載率と共有: 積載率は「配車済みの車両にどれだけ積んでいるか」なので未設定を
   * 除いて合算する（下の rateCapacity*）。共有すると片方を旧 /plans に合わせた途端、
   * もう片方の判定が変わってしまう。
   */
  const { totalCapacityCases, totalCapacityKg, totalCapacityM3 } = useMemo(() => {
    const seen = new Set<string>();
    let cases = 0;
    let kg = 0;
    let m3 = 0;
    let casesKnown = true;
    let kgKnown = true;
    let m3Known = true;
    rowGroups.forEach((g) => {
      if (seen.has(g.vehicle.id)) return;
      // 配車実績がある (real row) 車両のみキャパに数える
      const hasReal = g.rows.some((r) => !r.source.isPlaceholderRow);
      if (!hasReal) return;
      seen.add(g.vehicle.id);
      if (g.vehicle.maxCapacity == null) casesKnown = false;
      else cases += g.vehicle.maxCapacity;
      if (g.vehicle.maxCapacityKg == null) kgKnown = false;
      else kg += g.vehicle.maxCapacityKg;
      if (g.vehicle.maxCapacityM3 == null) m3Known = false;
      else m3 += g.vehicle.maxCapacityM3;
    });
    return {
      totalCapacityCases: casesKnown ? cases : null,
      totalCapacityKg: kgKnown ? kg : null,
      totalCapacityM3: m3Known ? m3 : null,
    };
  }, [rowGroups]);

  /**
   * 積載率の分母。未設定 (null) の車両は除いて合算する（旧 /plans と同じ）。
   * 1 台でも未設定なら軸ごと捨てる作りだと、キャパ未入力の車両が 1 台混ざるだけで
   * 積載率が出なくなる。
   */
  const { rateCapacityKg, rateCapacityM3 } = useMemo(() => {
    const seen = new Set<string>();
    let kg = 0;
    let m3 = 0;
    let kgKnown = false;
    let m3Known = false;
    rowGroups.forEach((g) => {
      if (seen.has(g.vehicle.id)) return;
      const hasReal = g.rows.some((r) => !r.source.isPlaceholderRow);
      if (!hasReal) return;
      seen.add(g.vehicle.id);
      if (g.vehicle.maxCapacityKg != null) { kg += g.vehicle.maxCapacityKg; kgKnown = true; }
      if (g.vehicle.maxCapacityM3 != null) { m3 += g.vehicle.maxCapacityM3; m3Known = true; }
    });
    return {
      rateCapacityKg: kgKnown ? kg : null,
      rateCapacityM3: m3Known ? m3 : null,
    };
  }, [rowGroups]);

  // 数量の分母は勤務 (delivery) ごとの最大積載数量を合算する（旧 /plans と同じ基準。
  // 車両単位だと同じトラックの 2 勤務目が数えられない）
  const rateCapacityCases = useMemo(
    () => (deliveries ? sumCapacityQuantity(deliveries) : null),
    [deliveries],
  );

  // 積載率の分子。同時に載っている量のピーク（旧 /plans の totalMaxLoad* と同じ）
  const peakLoads = useMemo(() => (deliveries ? sumPeakLoads(deliveries) : null), [deliveries]);

  // 配車サマリの集計 (deliveries ベース、実値で計算)
  // 売上/支払 は案件 (leg_run) の請求・支払金額を合算する（旧 /plans と同じ集計元）。
  // shift_truck_tariff_results だけを見ていたため、伝票詳細で入力した金額が出なかった
  // 距離は sumDistanceMm（走行距離 + 帰着地までの距離）。V3Plans と同じ計算式
  // 時間は各 delivery の min(operations.arrivalAt) → max(operations.arrivalAt) を合算 (V3 仕様)
  const summaryTotals = useMemo(() => {
    if (!deliveries || deliveries.length === 0) {
      return {
        quantity: 0,
        weightKg: 0,
        volumeM3: 0,
        revenueYen: 0,
        paymentYen: 0,
        distanceKm: 0,
        durationHours: 0,
      };
    }
    let quantity = 0;
    let weightKg = 0;
    let volumeM3 = 0;
    let durationSeconds = 0;
    const { billingYen: revenueYen, paymentYen } = sumLegRunAmounts(deliveries);
    for (const d of deliveries) {
      // 各 delivery 内の operations の最初・最後の arrivalAt の差を勤務時間とする (V3 と同様)
      let minArrivalMs = Infinity;
      let maxArrivalMs = -Infinity;
      for (const op of d.operations) {
        const t = new Date(op.arrivalAt).getTime();
        if (Number.isFinite(t)) {
          if (t < minArrivalMs) minArrivalMs = t;
          if (t > maxArrivalMs) maxArrivalMs = t;
        }
      }
      if (Number.isFinite(minArrivalMs) && Number.isFinite(maxArrivalMs) && maxArrivalMs > minArrivalMs) {
        durationSeconds += (maxArrivalMs - minArrivalMs) / 1000;
      }
      for (const lr of d.shipmentOrderLegRuns) {
        quantity += lr.item_count ?? 0;
        weightKg += lr.item_total_weight_kg ?? 0;
        volumeM3 += lr.item_total_volume_m3 ?? 0;
      }
    }
    return {
      quantity,
      weightKg,
      volumeM3,
      revenueYen,
      paymentYen,
      distanceKm: sumDistanceMm(deliveries) / 1_000_000,
      durationHours: Math.round((durationSeconds / 3600) * 10) / 10,
    };
  }, [deliveries]);
  const assignedTotal = summaryTotals.quantity;
  const assignedWeightKg = summaryTotals.weightKg;
  const assignedVolumeM3 = summaryTotals.volumeM3;

  // 未割当の合計 (数量・重量・体積)
  const unassignedAggregates = useMemo(() => {
    let cases = 0;
    let weightKg = 0;
    let volumeM3 = 0;
    for (const it of items) {
      cases += it.cases ?? 0;
      weightKg += it.weight ?? 0;
      volumeM3 += it.volume ?? 0;
    }
    return { cases, weightKg, volumeM3 };
  }, [items]);
  const totalCargoCases = assignedTotal + unassignedAggregates.cases;

  // 配車不成立判定: 当日案件総量 (assigned + unassigned) のいずれか (数量・重量・体積) が
  // トラック総キャパを超えていれば不成立。キャパが不明 (null) の軸は判定対象外
  const isFeasible = useMemo(() => {
    const totalCases = assignedTotal + unassignedAggregates.cases;
    const totalWeightKg = assignedWeightKg + unassignedAggregates.weightKg;
    const totalVolumeM3 = assignedVolumeM3 + unassignedAggregates.volumeM3;
    if (totalCapacityCases != null && totalCases > totalCapacityCases) return false;
    if (totalCapacityKg != null && totalWeightKg > totalCapacityKg) return false;
    if (totalCapacityM3 != null && totalVolumeM3 > totalCapacityM3) return false;
    return true;
  }, [assignedTotal, assignedWeightKg, assignedVolumeM3, unassignedAggregates, totalCapacityCases, totalCapacityKg, totalCapacityM3]);

  // 選択された未割当の合計（選択 / 合計 形式で使う）
  const selectedItems = useMemo(
    () => items.filter((it) => selectedItemIds.has(it.id)),
    [items, selectedItemIds],
  );

  const filteredAndSortedItems = useMemo(() => {
    const normalized = committedSearchText.trim().toLowerCase();
    // 検索対象は詳細フィルターで選べる列すべて。source を持たないカードは列を引けないので
    // 従来どおり降地名称だけで判定する（0 件に消してしまわないため）
    let result = normalized
      ? items.filter((it) => (it.source
        ? matchesFreeText(it.source, normalized, filterColumns)
        : it.storeName.toLowerCase().includes(normalized)))
      : [...items];

    result = filterItemsByRows(result, advancedFilters, filterColumnsByKey);
    // 表示対象（配送形態・区間種別）。詳細フィルターと同じく一覧だけに効かせ、サマリは全件で出す
    result = filterUnassignedItemsByOrderAttrs(result, orderAttrFilter);

    // ソート用の値抽出。実値が無いカード (null) はソート時に最小値扱い (= 0) とする。
    const getValue = (it: UnassignedItem) => {
      switch (sortField) {
        case 'quantity': return it.cases ?? 0;
        case 'weight': return it.weight ?? 0;
        case 'volume': return it.volume ?? 0;
      }
    };

    result.sort((a, b) => {
      const diff = getValue(a) - getValue(b);
      return sortOrder === 'asc' ? diff : -diff;
    });

    return result;
  }, [items, committedSearchText, sortField, sortOrder, advancedFilters, filterColumns, filterColumnsByKey, orderAttrFilter]);

  /**
   * 「選択中のみ表示」。選んだ案件が一覧に散っていて確認しづらいため（指摘 2026/08/19）。
   *
   * Why not filteredAndSortedItems に混ぜる: 選択は 1 クリックごとに変わるので、
   * 検索・フィルタ・ソートまで巻き込んで組み直すことになる。ここは絞るだけの別 memo にする。
   *
   * Why not 選択ゼロでも絞る: 全解除した瞬間に空の一覧になり、チェックを外す手がかりまで
   * 消える。選択が無いときは絞り込みを掛けない（チェックボックスも出さない）。
   */
  const [selectedOnly, setSelectedOnly] = useState(false);
  const visibleItems = useMemo(
    () => (selectedOnly && selectedItemIds.size > 0
      ? filteredAndSortedItems.filter((it) => selectedItemIds.has(it.id))
      : filteredAndSortedItems),
    [filteredAndSortedItems, selectedOnly, selectedItemIds],
  );

  // レーンモードのパイプラインは filter → search → group → aggregate → sort(グループ)。
  // フィルタ・検索の評価は伝票単位のまま（P2a の契約を変えない）。
  const laneGroups = useMemo(
    () => (displayMode === 'lane'
      ? sortLaneGroups(buildLaneGroups(visibleItems, deliveryListGrouping), sortField, sortOrder)
      : []),
    [displayMode, visibleItems, sortField, sortOrder, deliveryListGrouping],
  );

  const hiddenSelectedCount = useMemo(
    () => countHiddenSelected(items, visibleItems, selectedItemIds),
    [items, visibleItems, selectedItemIds],
  );

  useEffect(() => {
    onHiddenSelectedCountChange?.(hiddenSelectedCount);
  }, [hiddenSelectedCount, onHiddenSelectedCountChange]);

  // 伝票カードの prop 解決をここに閉じ込める。レーンモード（LaneCard）も同じ関数を使うので、
  // 画面差 prop の素通し漏れが原理的に起きない。
  const renderDraggableCardItem = (item: UnassignedItem) => {
    const m = /^lr-(\d+)$/.exec(item.id);
    const legRunId = m ? parseInt(m[1], 10) : null;
    const reasons = legRunId != null ? notAllocReasonsByOrderId?.get(legRunId) : undefined;
    return (
      <DraggableCardItem
        key={item.id}
        item={item}
        displayMetrics={displayMetrics}
        customInputFieldNames={customInputFieldNames}
        screenName={screenName}
        readOnly={readOnly}
        selected={selectedItemIds.has(item.id)}
        onSelectItem={onSelectItem}
        onShowDetail={onShowDetail}
        notAllocReasons={reasons}
        notAllocDeliveries={notAllocDeliveries}
        notAllocTrucks={notAllocTrucks}
        notAllocDrivers={notAllocDrivers}
        onRestoreSplit={onRestoreSplit}
        restoreSplitDisabledReason={restoreSplitDisabledReason}
      />
    );
  };

  return (
    <UnassignedDropArea>
      {/* Fixed header */}
      <Box sx={{ flexShrink: 0 }}>
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <DispatchStatusCard
            totalCargoCases={totalCargoCases}
            totalCargoWeightKg={assignedWeightKg + unassignedAggregates.weightKg}
            totalCargoVolumeM3={assignedVolumeM3 + unassignedAggregates.volumeM3}
            totalCapacityCases={totalCapacityCases}
            totalCapacityKg={totalCapacityKg}
            totalCapacityM3={totalCapacityM3}
            uniqueTruckCount={uniqueTruckCount}
            totalRotations={totalRotations}
            isFeasible={isFeasible}
            displayMetrics={displayMetrics}
            screenName={screenName}
          />
        </Box>

        <Box sx={{ px: 2, pb: 1 }}>
          <DispatchSummaryCard
            totalQuantity={summaryTotals.quantity}
            totalWeightKg={summaryTotals.weightKg}
            totalVolumeM3={summaryTotals.volumeM3}
            peakQuantity={peakLoads?.cases ?? 0}
            peakWeightKg={peakLoads?.kg ?? 0}
            peakVolumeM3={peakLoads?.m3 ?? 0}
            totalCapacityCases={rateCapacityCases}
            totalCapacityKg={rateCapacityKg}
            totalCapacityM3={rateCapacityM3}
            totalRevenueYen={summaryTotals.revenueYen}
            totalPaymentYen={summaryTotals.paymentYen}
            totalDistanceKm={summaryTotals.distanceKm}
            totalDurationHours={summaryTotals.durationHours}
            screenName={screenName}
            onOpenTariffCalculation={onOpenTariffCalculation}
            tariffCalculationDisabledReason={tariffCalculationDisabledReason}
          />
        </Box>

        <Box sx={{ px: 2, pb: 1 }}>
          <UnassignedStatsCard items={items} selectedItems={selectedItems} screenName={screenName} />
        </Box>

        {/* 検索 + フィルター */}
        <Box sx={{ px: 2, pt: 0.5, pb: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ position: 'relative', flex: 1 }}>
            <SearchRounded sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: 'text.secondary', pointerEvents: 'none' }} />
            <InputBase
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
              onCompositionStart={onCompositionStart}
              onCompositionEnd={(e) => handleCompositionEnd((e.target as HTMLInputElement).value)}
              placeholder="検索"
              sx={{
                width: '100%',
                height: 32,
                pl: 4,
                pr: 1.5,
                fontSize: 12,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'background.paper',
              }}
            />
          </Box>
          {/* 並び替え・フィルター・保存済みフィルターは 1 つの Popover に同居させる
              （サンプル準拠。分けると幅が足りずボタン文字が縦に折り返す）。
              表示単位トグルは下の「全選択」行へ置く。 */}
          <Tooltip title="並び替え・フィルター" placement="top">
            <Box component="span" sx={{ position: 'relative' }}>
              <IconButton
                size="small"
                // Tooltip は Box を包むので title が IconButton の名前にならない。明示する。
                aria-label="並び替え・フィルター"
                onClick={(e) => {
                  ReactGA.event('click', {
                    screen_name: screenName,
                    button_name: '並び替え・フィルター',
                  });
                  setFilterAnchor(e.currentTarget);
                }}
                sx={{
                  width: 32,
                  height: 32,
                  border: 1,
                  borderColor: activeFilterCount > 0 ? 'primary.main' : 'divider',
                  color: activeFilterCount > 0 ? 'primary.main' : 'text.primary',
                  borderRadius: 1,
                }}
              >
                <TuneRounded sx={{ fontSize: 14 }} />
              </IconButton>
              {activeFilterCount > 0 && (
                <Box
                  component="span"
                  sx={{
                    position: 'absolute', right: -4, top: -4,
                    display: 'flex', height: 16, width: 16,
                    alignItems: 'center', justifyContent: 'center',
                    borderRadius: '50%', bgcolor: 'hsl(var(--primary))',
                    fontSize: '9px', fontWeight: 700, color: 'hsl(var(--primary-foreground))',
                  }}
                >
                  {activeFilterCount}
                </Box>
              )}
            </Box>
          </Tooltip>
          <AdvancedFilterPopover
            open={filterOpen}
            anchorEl={filterAnchor}
            onClose={() => setFilterAnchor(null)}
            columns={filterColumns}
            rows={advancedFilters}
            onRowsChange={setAdvancedFilters}
            companyId={companyId}
            isLicenseReady={isLicenseReady}
            isMastersReady={isMastersReady}
            isMastersError={isMastersError}
            onNotify={(message) => enqueueSnackbar(message, { variant: 'info' })}
            sortField={sortField}
            sortOrder={sortOrder}
            onSortFieldChange={setSortField}
            onSortOrderChange={setSortOrder}
            screenName={screenName}
            presets={serverPresets.presets}
            onSavePreset={handleSavePreset}
            onDeletePreset={handleDeletePreset}
            // 取り込みは savePreset をそのまま渡す。保存の成否はダイアログが受け取って
            // 「成功してから適用して閉じる」を守るので、ここで snackbar に包まない
            onSaveImportedPreset={serverPresets.savePreset}
          />
        </Box>

        {/* 全選択 + 固定ルートに対応づけ */}
        <Box sx={{ px: 1.5, py: 0.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid hsl(var(--border) / 0.4)' }}>
          {/* サンプルコード (route-planner UnassignedPanel) と揃えて、Button で 全選択 / 全解除 をトグル表示する */}
          <Button
            size="small"
            onClick={() => {
              const allSelected = visibleItems.length > 0
                && visibleItems.every((it) => selectedItemIds.has(it.id));
              ReactGA.event('click', {
                screen_name: screenName,
                button_name: '未割当 全選択',
                label: allSelected ? 'OFF' : 'ON',
              });
              visibleItems.forEach((it) => {
                const isSel = selectedItemIds.has(it.id);
                // 全選択中なら全解除、そうでなければ不足分を選択
                if (allSelected) {
                  if (isSel) onSelectItem(it.id);
                } else if (!isSel) {
                  onSelectItem(it.id);
                }
              });
            }}
            sx={{ height: 24, px: 1, fontSize: 11, textTransform: 'none', color: 'text.primary', minWidth: 0, whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {(visibleItems.length > 0 && visibleItems.every((it) => selectedItemIds.has(it.id))) ? '全解除' : '全選択'}
          </Button>
          {selectedItemIds.size > 0 && (
            <Box component="span" sx={{ fontSize: '11px', color: 'hsl(var(--muted-foreground))', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {/* 非表示件数は出さない。自動配車ダイアログ側の警告
                  (PlanningPresenter の hiddenSelectedOrderCount) が同じことを伝える */}
              {`${selectedItemIds.size}件選択中`}
            </Box>
          )}
          {/* readOnly ではなく handler の有無で決める。/map は readOnly だが削除は使える
              （配車リセット・確定ボタンと同じ分離の仕方） */}
          {selectedItemIds.size > 0 && onDeleteSelected && (
            <Tooltip title="選択した案件を削除" arrow>
              <IconButton
                size="small"
                onClick={() => {
                  ReactGA.event('click', {
                    screen_name: screenName,
                    button_name: '未割当案件 削除',
                  });
                  onDeleteSelected();
                }}
                aria-label="選択した案件を削除"
                sx={{ p: 0.25, color: 'hsl(var(--destructive))', flexShrink: 0 }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {selectedItemIds.size > 0 && (
            <Tooltip title="選択中の案件だけを表示" arrow>
              <Checkbox
                size="small"
                checked={selectedOnly}
                onChange={(e) => {
                  ReactGA.event('click', {
                    screen_name: screenName,
                    button_name: '未割当 選択中のみ表示',
                    label: e.target.checked ? 'ON' : 'OFF',
                  });
                  setSelectedOnly(e.target.checked);
                }}
                inputProps={{ 'aria-label': '選択中の案件だけを表示' }}
                sx={{ p: 0.25, flexShrink: 0 }}
              />
            </Tooltip>
          )}
          <Box sx={{ flex: 1 }} />
          {/* 表示単位（伝票 / 地点集約）。サンプル準拠で「全選択」行の右側に置く。
              convenience は同じ行に「固定ルートに対応づけ」があるため出さない（/map 専用）。 */}
          {displayModeSwitchable && (
          <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
            {([['invoice', '伝票単位'], ['lane', '地点単位']] as const).map(([mode, label]) => (
              <Box
                key={mode}
                component="button"
                type="button"
                onClick={() => {
                  ReactGA.event('click', { screen_name: screenName, button_name: '未割当 表示単位', label: mode });
                  setDisplayMode(mode);
                }}
                sx={{ ...sortBtnSx(displayMode === mode), px: 1, py: 0.25, fontSize: 11, whiteSpace: 'nowrap' }}
              >
                {label}
              </Box>
            ))}
          </Stack>
          )}
          {!readOnly && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<LinkRounded sx={{ fontSize: 14 }} />}
              // 固定ルートが読み込まれていないときは disabled（= onAlignToFixedRoute 未指定）
              disabled={!onAlignToFixedRoute || isAligningToFixedRoute}
              // MouseEvent が引数に渡らないよう arrow でラップ
              onClick={() => {
                ReactGA.event('click', {
                  screen_name: screenName,
                  button_name: '固定ルートに対応づけ',
                });
                onAlignToFixedRoute?.();
              }}
              sx={{ height: 24, px: 1, fontSize: 11, textTransform: 'none', minWidth: 0, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {isAligningToFixedRoute ? '対応づけ中...' : '固定ルートに対応づけ'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Card list */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          px: 1.5,
          pb: 1.5,
          pt: 1,
          '& > * + *': { mt: 1 },
        }}
      >
        {isLoading && (
          <Stack spacing={1}>
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} variant="rectangular" height={70} sx={{ borderRadius: 1 }} />
            ))}
          </Stack>
        )}

        {!isLoading && displayMode === 'invoice' && visibleItems.map(renderDraggableCardItem)}

        {!isLoading && displayMode === 'lane' && laneGroups.map((group) => {
          const selectedCount = group.items.filter((it) => selectedItemIds.has(it.id)).length;
          return (
            <LaneCard
              key={group.key}
              group={group}
              displayMetrics={displayMetrics}
              customInputFieldNames={customInputFieldNames}
              selectedCount={selectedCount}
              onToggleSelection={() => {
                ReactGA.event('click', { screen_name: screenName, button_name: 'レーン 選択' });
                // 全件選択済みなら全解除、そうでなければ不足分だけ選択（親の toggle 契約に合わせる）。
                const allSelected = selectedCount === group.items.length;
                group.items.forEach((it) => {
                  const isSelected = selectedItemIds.has(it.id);
                  if (allSelected ? isSelected : !isSelected) onSelectItem(it.id);
                });
              }}
              notAllocReasons={mergeLaneNotAllocReasons(group.items, notAllocReasonsByOrderId)}
              notAllocDeliveries={notAllocDeliveries}
              notAllocTrucks={notAllocTrucks}
              notAllocDrivers={notAllocDrivers}
              onShowDetail={onShowDetail && (() => {
                ReactGA.event('click', { screen_name: screenName, button_name: 'レーン 詳細表示' });
                // rect は mapTopRight では使わないが、口の形を伝票カードと揃えるため渡す
                onShowDetail(buildLaneDetailItem(group), new DOMRect());
              })}
            />
          );
        })}

        {!isLoading && visibleItems.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center', fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>
            {items.length === 0 ? 'すべて割当済み' : '検索条件に一致する未割当はありません'}
          </Box>
        )}
      </Box>
    </UnassignedDropArea>
  );
});

export default ConvenienceDispatchUnassignedList;
